// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type { GetFinancialsTableData, GetFinancialsTableErrors, GetFinancialsTableResponses, PatchFinancialsTableData, PatchFinancialsTableErrors, PatchFinancialsTableResponses } from './types.gen';
import { zGetFinancialsTableHeaders, zGetFinancialsTablePath, zGetFinancialsTableResponse, zPatchFinancialsTableBody, zPatchFinancialsTableHeaders, zPatchFinancialsTablePath, zPatchFinancialsTableResponse } from './zod.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean, TResponse = unknown> = Options2<TData, ThrowOnError, TResponse> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Get the Financials table for an RFP
 *
 * Returns the denormalized payload that powers the
 * Financials sub-tab of the *Present* tab.
 *
 * The response contains the following top-level collections:
 * - `coverages` — one entry per requested coverage, in
 * display order (`order`).
 * - `periods` — fixed object with `current`, `renewal`, and
 * `revisedRenewal` slots. Each slot owns its per-coverage
 * annual premium values plus a `hidden` flag.
 * - `carriers` — one entry per carrier the RFP was sent to,
 * in display order. Each carrier owns its per-coverage
 * annual premium and (optional) per-cell note.
 * - `view` — per-axis indication of where the returned
 * `coverages` / `carriers` ordering and visibility came
 * from (page-local vs. broker-global).
 * - `brokerConfig` — broker-scoped display preferences
 * (e.g., whether the dollar-savings rows should render).
 * - `tablePreferences` — opaque AG Grid presentation state
 * (column widths, etc.) the FE round-trips through MPX's
 * existing grid-state mechanism.
 *
 * Per-cell values are stored on the **owning column**: a
 * period column owns its `annualPremium` per coverage; a
 * carrier column owns its `annualPremium` and optional
 * `note` per coverage. The `coverages` collection is
 * metadata only.
 *
 * Returns `200` with empty `coverages` / `carriers` arrays
 * only when the RFP exists but has no requested coverages
 * and no invited carriers. Otherwise the populated arrays
 * describe the grid the FE should render.
 *
 */
export const getFinancialsTable = <ThrowOnError extends boolean = false>(options: Options<GetFinancialsTableData, ThrowOnError>) => (options.client ?? client).get<GetFinancialsTableResponses, GetFinancialsTableErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: z.never().optional(),
        headers: zGetFinancialsTableHeaders.optional(),
        path: zGetFinancialsTablePath,
        query: z.never().optional()
    }).parseAsync(data),
    responseType: 'json',
    responseValidator: async (data) => await zGetFinancialsTableResponse.parseAsync(data),
    url: '/rfps/{id}/financials-table',
    ...options
});

/**
 * Apply a sparse update to the Financials table for an RFP
 *
 * Sparse update that mirrors the shape of
 * `GetFinancialsTableResponse`. Every top-level key is
 * optional, every nested key is optional.
 *
 * Sparse semantics:
 * - **Omit a key** to leave its current value untouched.
 * - **Send `null`** for an editable scalar to clear it
 * (e.g., `annualPremium: null` to remove a quoted
 * premium; `note: null` to delete a note).
 * - **Send a value** to set/replace it. For `order` and
 * `hidden`, this is an absolute set, not a delta.
 *
 * Page-local vs global writes:
 * - The `view.coveragesApplyTo` / `view.carriersApplyTo`
 * fields are only meaningful when the same PATCH also
 * includes the corresponding `coverages` / `carriers`
 * payload. The BFF rejects a PATCH that sets one without
 * the other (HTTP `400`).
 * - Ordinary edits (typical autosave flushes) omit `view`
 * entirely and are treated as page-local.
 *
 * Validation rules enforced server-side:
 * - `annualPremium` is a non-negative integer (whole
 * dollars) when set; `null` is allowed to clear.
 * - `note` is plain text up to 1024 characters when set;
 * `null` is allowed to clear.
 * - `order` values within an axis must be unique
 * non-negative integers and form a permutation of the
 * existing entries on that axis (no inserts or deletes
 * via PATCH).
 * - Unknown coverage / carrier IDs are rejected.
 *
 * Returns `204 No Content` on success — the FE refetches
 * via React Query when it needs the updated cache.
 *
 */
export const patchFinancialsTable = <ThrowOnError extends boolean = false>(options: Options<PatchFinancialsTableData, ThrowOnError>) => (options.client ?? client).patch<PatchFinancialsTableResponses, PatchFinancialsTableErrors, ThrowOnError>({
    requestValidator: async (data) => await z.object({
        body: zPatchFinancialsTableBody,
        headers: zPatchFinancialsTableHeaders.optional(),
        path: zPatchFinancialsTablePath,
        query: z.never().optional()
    }).parseAsync(data),
    responseValidator: async (data) => await zPatchFinancialsTableResponse.parseAsync(data),
    url: '/rfps/{id}/financials-table',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});
